import { ref, reactive, computed } from 'vue'
import { defineStore } from 'pinia'
import { jwtDecode } from 'jwt-decode'
import { FetchError } from 'ofetch'
import { useUserClientStores } from '@/composables/api/useUserClientStores'
import { useGiftRepository } from '@/composables/api/repositories/gift'
import { useOtpRepository } from '@/composables/api/repositories/otp'
import { useClientStoreRepository } from '@/composables/api/repositories/client-store'
import type { CampaignGiftGetResponseBody } from '@/types/redemption'
import { useIntervalFn } from '@vueuse/core'
import { piniaPluginPersistedstate, useCookie } from '#imports'
import type {
  StoreSearchParams,
  StoreSearchResponse,
} from '~/composables/api/useStore'

const GIFT_DATA_EXPIRY = 60 * 60 * 1000 // 1 hour in milliseconds
const REFRESH_INTERVAL = 60000 // 1 minute

export const useGuestStore = defineStore(
  'guest',
  () => {
    const guest = reactive<{
      email: string | undefined
      storeId: string | undefined
      userId: string | undefined
      token: string | undefined
      loggedInToken: string | undefined
      store:
        | (StoreSearchParams & { currentCatalog: 'vision' | 'safety' })
        | undefined
    }>({
      email: undefined,
      storeId: undefined,
      userId: undefined,
      token: undefined,
      loggedInToken: undefined,
      store: undefined,
    })

    const gift = ref<CampaignGiftGetResponseBody | null>(null)
    const dependents = ref<CampaignGiftGetResponseBody[] | null>(null)
    const currentGiftId = ref<string>('')
    const lastGiftFetchTimestamp = ref<number>(0)
    const lastDependentFetchTimestamp = ref<number>(0)

    const currentGift = computed<CampaignGiftGetResponseBody | null>(() => {
      if (isGuestMode.value) {
        return gift.value
      }
      if (gift.value && gift.value.id === currentGiftId.value) {
        return gift.value
      }

      return dependents.value?.find((g) => g.id === currentGiftId.value) || null
    })

    const isAdminGift = computed<boolean>(() => {
      return gift.value?.adminPurchase ?? gift.value?.recipientId === 'admin'
    })

    const loading = computed<boolean>(() => {
      if (import.meta.server) {
        return true
      }
      return (
        (!isAdminGift.value && dependents.value === null) ||
        gift.value === null ||
        !(guest.token || guest.loggedInToken)
      )
    })

    const isSafetyCatalog = computed(
      () =>
        currentGift.value?.policy?.catalogs?.includes('safety') ||
        guest.store?.currentCatalog === 'safety' ||
        false,
    )

    const setCurrentGift = (targetGiftId: string) => {
      currentGiftId.value = targetGiftId
    }

    const setLoggedInToken = (token: string) => {
      guest.loggedInToken = token
    }

    const { switchStore } = useUserClientStores()
    const switchStoreToken = async (storeId: string) => {
      if (guest.loggedInToken) {
        requestError.value = null
        try {
          const response = await clientStoreApi.execute<'switchStore'>(
            clientStoreApi.switchStore(storeId),
          )
          if (response?.token) {
            setLoggedInToken(response.token)
          }
        } catch (error) {
          const fetchError = clientStoreApi.getError(error)
          if (fetchError) {
            requestError.value = fetchError
          }
        }
      }
    }

    const calculateGiftAmt = (g: CampaignGiftGetResponseBody): number => {
      if (g.usage?.type === 'freeItem') {
        return g.usage.budget ?? 0
      } else if (g.usage?.type === 'giftCard') {
        return g.usage.balance ?? 0
      }
      return 0
    }

    const giftAmount = computed(() =>
      gift.value ? calculateGiftAmt(gift.value) : 0,
    )

    const requestError = ref<FetchError | null>()

    const giftApi = useGiftRepository()
    const otpApi = useOtpRepository()
    const clientStoreApi = useClientStoreRepository()

    const loadDependents = async (email?: string) => {
      if (isAdminGift.value) {
        dependents.value = []
        return
      }
      const emailToUse = email || guest.email || ''
      try {
        const response = await giftApi.execute<'getDependentGifts'>(
          giftApi.getDependentGifts(emailToUse),
        )
        if (response) {
          dependents.value = []
          response.forEach((g) => {
            if (g.id !== gift.value?.id) {
              dependents.value?.push(g)
            }
          })
          lastDependentFetchTimestamp.value = Date.now()
        }
      } catch (error) {
        const fetchError = giftApi.getError(error)
        if (fetchError) {
          console.error(fetchError)
        }
        dependents.value = []
      }
    }

    const loadGift = async (id: string) => {
      const employerAccessToken = useCookie('employer_access_token')
      const employerStoreId = useCookie('employer_store_id')
      const employerCampaignId = useCookie('employer_campaign_id')

      try {
        const response = await giftApi.execute<'getAdminGift' | 'getGiftById'>(
          employerAccessToken.value
            ? giftApi.getAdminGift(
                employerCampaignId.value || '',
                employerStoreId.value || '',
              )
            : giftApi.getGiftById(id),
        )

        if (response) {
          guest.store = undefined
          gift.value = response
          ;({
            email: guest.email,
            storeId: guest.storeId,
            userId: guest.userId,
          } = response)

          if (!currentGiftId.value) {
            currentGiftId.value = response.id || ''
          }
          lastGiftFetchTimestamp.value = Date.now()
        }
      } catch (error) {
        const fetchError = giftApi.getError(error)
        if (fetchError) {
          requestError.value = fetchError
        }
      }
    }

    const recreateCatalogFilter = (catalog?: string) => {
      if (!catalog) return {}
      return {
        product: `(Redemption_Catalog:${catalog})`,
        'product-variant': `(Redemption_Catalog_search:${catalog})`,
      }
    }

    const setGuestGift = (
      storeData: StoreSearchResponse,
      storeParams: StoreSearchParams,
    ) => {
      const currentCatalog =
        guest.store?.currentCatalog ??
        (storeData.catalog.length === 1
          ? (storeData.catalog[0] as 'vision' | 'safety')
          : 'vision')
      gift.value = {
        storeId: storeData.storeId,
        code: storeData.storeCode,
        storeName: storeData.storeName,
        catalogFilter: recreateCatalogFilter(guest.store?.currentCatalog),
        catalogs: storeData.catalog,
        partnerId: storeData.partnerId,
        algoliaIndex: storeData.algoliaIndex,
        frameFilter: storeData.frameFilter,
      }
      guest.storeId = storeData.storeId
      guest.store = {
        ...storeParams,
        currentCatalog,
      }
    }

    const switchSelectedCatalog = (catalog: 'vision' | 'safety') => {
      if (guest.store) {
        guest.store.currentCatalog = catalog
        gift.value = {
          ...gift.value,
          catalogFilter: recreateCatalogFilter(catalog),
        }
      }
    }

    const isGuestMode = computed(
      () => !gift.value?.id || typeof guest.store?.partnerName === 'string',
    )

    const guestStoreUrl = computed(() => {
      if (!guest.store?.partnerName) {
        return '/'
      }
      const { partnerName, partnerBrand, partnerRegion } = guest.store
      const filteredParams = [partnerName, partnerBrand, partnerRegion].filter(
        Boolean,
      )
      return `/${filteredParams.join('/')}`
    })

    const hasMultipleGuestCatalogs = computed(
      () => (gift.value?.catalogs?.length || 0) > 1,
    )

    useIntervalFn(async () => {
      const shouldRefreshGift =
        Date.now() - lastGiftFetchTimestamp.value > GIFT_DATA_EXPIRY

      if (guest.token && gift.value?.id && shouldRefreshGift) {
        await loadGift(gift.value.id)
      }
    }, REFRESH_INTERVAL)

    useIntervalFn(async () => {
      const shouldRefreshDependents =
        Date.now() - lastDependentFetchTimestamp.value > GIFT_DATA_EXPIRY

      if (guest.token && guest.email && shouldRefreshDependents) {
        await loadDependents(guest.email)
      }
    }, REFRESH_INTERVAL)

    const loginGuestUser = async () => {
      try {
        const response = await otpApi.execute<'guestLogin'>(
          otpApi.guestLogin({
            userEmail: guest.email ?? '',
            userId: guest.userId ?? '',
            storeId: guest.storeId ?? '',
          }),
        )
        if (response?.token) {
          guest.token = response.token
        }
      } catch (error) {
        const fetchError = otpApi.getError(error)
        if (fetchError) {
          requestError.value = fetchError
        }
      }
    }

    const reset = () => {
      gift.value = null
      currentGiftId.value = ''
      dependents.value = null
      guest.email = undefined
      guest.storeId = undefined
      guest.userId = undefined
      guest.token = undefined
      guest.loggedInToken = undefined
      requestError.value = null
      lastGiftFetchTimestamp.value = 0
      lastDependentFetchTimestamp.value = 0
      guest.store = undefined
    }

    const primaryUser = computed<{
      firstName: string
      lastName: string
      email: string
      applicationId: string
    } | null>(() => {
      if (guest.loggedInToken) {
        const decodedToken = jwtDecode<{
          firstName: string
          lastName: string
          email: string
          applicationId: string
        }>(guest.loggedInToken)
        return decodedToken
      }
      return null
    })

    return {
      loading,
      guest,
      primaryUser,
      gift,
      currentGiftId,
      currentGift,
      isSafetyCatalog,
      setCurrentGift,
      setLoggedInToken,
      switchStoreToken,
      dependents,
      giftAmount,
      requestError,
      loadGift,
      loadDependents,
      loginGuestUser,
      reset,
      calculateGiftAmt,
      setGuestGift,
      switchSelectedCatalog,
      hasMultipleGuestCatalogs,
      isGuestMode,
      guestStoreUrl,
      isAdminGift,
    }
  },
  {
    persist: [
      {
        storage: piniaPluginPersistedstate.cookies({
          sameSite: 'strict',
        }),
        pick: ['guest', 'gift', 'currentGiftId', 'lastFetchTimestamp'],
        omit: [
          'gift.createAt',
          'gift.updateAt',
          'gift.deleteAt',
          'gift.algoliaIndex',
          'gift.schedule',
          'gift.userEmail',
        ],
      },
    ],
  },
)
